/**
 * 커리큘럼 시나리오 상세 — Google 스프레드시트 **old_scenario_data** 탭과 동기화
 * (메인 콘텐츠관리 탭 전체가 아님)
 * @see https://docs.google.com/spreadsheets/d/1kJc2ljIZpu3KWdbBuGf7SQTNKVLK9Tp9MyTPOuBg4lU/
 *
 * **일괄 관리**: `content_code`·시나리오 ID·제목 등은 아래 `CurriculumSheetRemainderRow` 테이블들에서만 수정한다.
 */
import type { ChecklistItem, Scenario } from '../types/admin';

export type ScenarioDetail = Scenario;

export const triageResultMetadata = {
  stats: [
    { label: '정답율', value: '17', sub: '%', color: 'text-green-600' },
    { label: '정답수/전체', value: '1', sub: '/ 8', color: 'text-gray-600' },
    { label: '소요시간', value: '8:42', sub: '', color: 'text-gray-600' },
  ],
  triagePatients: [
    { patientName: '김건명', gender: '남성', age: '23세', correctAnswer: 'black', userAnswer: 'black', correctRate: 100, correctRateByColor: { blue: 0, red: 0, yellow: 2, green: 3, white: 0, black: 95 } },
    { patientName: '김영희', gender: '여성', age: '46세', correctAnswer: 'green', userAnswer: 'yellow', correctRate: 50, correctRateByColor: { blue: 4, red: 2, yellow: 48, green: 41, white: 3, black: 5 } },
    {
      patientName: '이하선', gender: '남성', age: '41세', correctAnswer: 'green', userAnswer: '',
      walkAbility: 'Yes', responsiveness: 'Yes',
      bloodPressure: '117/91',
      pulse: '100',
      respiration: '24',
      temperature: '38.8',
      spo2: '98',
      bloodGlucose: '90',
      vitalSigns: '117/91 mmHg, HR 100/min, RR 24/min, T 38.8°C, SpO2 98%, BST 90 mg/dl',
      majorInjuries: ['경미한 화상', '경미한 화상'], chiefComplaint: '없음', intervention: '미수행',
      comments: '환자의 빠른 발견과 처치는 환자의 생존에 직결됩니다. 다음 기회에는 구역을 더 꼼꼼히 찾아보시기 바랍니다.',
      correctRate: 33,
      correctRateByColor: { blue: 2, red: 1, yellow: 30, green: 51, white: 2, black: 14 }
    },
    { patientName: '오상태', gender: '남성', age: '37세', correctAnswer: 'red', userAnswer: '', correctRate: 0, correctRateByColor: { blue: 0, red: 88, yellow: 8, green: 2, white: 1, black: 1 } },
    { patientName: '정인선', gender: '여성', age: '56세', correctAnswer: 'green', userAnswer: '', correctRate: 75, correctRateByColor: { blue: 3, red: 1, yellow: 15, green: 72, white: 3, black: 6 } },
    { patientName: '권남일', gender: '남성', age: '16세', correctAnswer: 'yellow', userAnswer: '', correctRate: 25, correctRateByColor: { blue: 2, red: 5, yellow: 68, green: 18, white: 2, black: 5 } },
    { patientName: '박소생', gender: '남성', age: '68세', correctAnswer: 'blue', userAnswer: 'blue', correctRate: 100, correctRateByColor: { blue: 78, red: 6, yellow: 8, green: 5, white: 3, black: 0 } },
    { patientName: '최여유', gender: '여성', age: '34세', correctAnswer: 'white', userAnswer: 'green', correctRate: 0, correctRateByColor: { blue: 4, red: 5, yellow: 12, green: 42, white: 35, black: 2 } }
  ]
};

type TriageOpts = { handover?: string; learningObjectives?: string[] };

function triageScenario(id: number, title: string, duration: string, opts?: TriageOpts): ScenarioDetail {
  return {
    id,
    title,
    classId: '002',
    learningObjectives: opts?.learningObjectives ?? ['중증도분류'],
    duration,
    supportedDevices: ['Mobile'],
    videoUrl: '',
    handover: opts?.handover ?? '',
    keyTasks: [],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'triage',
    contentCode: 'triage',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: duration,
    averageScore: 0,
    platform: 'Mobile',
    checklist: [],
    resultMetadata: triageResultMetadata
  };
}

function radiationScenario(id: number, title: string, duration: string, opts?: TriageOpts): ScenarioDetail {
  return {
    id,
    title,
    classId: '003',
    learningObjectives: opts?.learningObjectives ?? ['중증도분류', '방사선 피폭·오염 계측 반영'],
    duration,
    supportedDevices: ['Mobile'],
    videoUrl: '',
    handover: opts?.handover ?? '',
    keyTasks: [],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'triage',
    contentCode: 'radiation',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: duration,
    averageScore: 0,
    platform: 'Mobile',
    checklist: [],
    resultMetadata: triageResultMetadata,
  };
}

function ktasScenario(
  id: number,
  title: string,
  duration: string,
  handover: string,
  learningObjectives: string[],
): ScenarioDetail {
  return {
    id,
    title,
    classId: '001',
    learningObjectives,
    duration,
    supportedDevices: ['PC'],
    videoUrl: '',
    handover,
    keyTasks: [],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'disaster-emergency',
    contentCode: 'ktas',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: duration,
    averageScore: 0,
    platform: 'PC',
    checklist: [],
    resultMetadata: triageResultMetadata,
  };
}

/** 절차형 시나리오 공통 `resultMetadata` 뼈대 — `stats`는 평가 요약 카드·테이블과 동일 3항목 */
function defaultProcedureResultMetadata(): NonNullable<Scenario['resultMetadata']> {
  return {
    stats: [
      { label: '정답율', value: '—', sub: '', color: 'text-gray-600' },
      { label: '정답수/전체', value: '—', sub: '', color: 'text-gray-600' },
      { label: '소요시간', value: '—', sub: '', color: 'text-gray-600' },
    ],
  };
}

export function procedureScenario(p: {
  id: number;
  classId: string;
  title: string;
  contentCode: string;
  category: string;
  platform: 'VR' | 'Mobile' | 'PC';
  duration: string;
  handover: string;
  learningObjectives: string[];
  /** 병합·덮어쓰기: `evaluationSummary`, `ivPerformance`, `diseaseCarePerformance` 등 */
  resultMetadata?: Record<string, unknown>;
}): ScenarioDetail {
  const dev = p.platform === 'VR' ? ['VR'] : p.platform === 'PC' ? ['PC'] : ['Mobile'];
  const baseRm = defaultProcedureResultMetadata();
  const resultMetadata = (
    p.resultMetadata != null
      ? ({ ...baseRm, ...p.resultMetadata } as NonNullable<Scenario['resultMetadata']>)
      : baseRm
  );
  return {
    id: p.id,
    title: p.title,
    classId: p.classId,
    learningObjectives: p.learningObjectives,
    duration: p.duration,
    supportedDevices: dev,
    videoUrl: '',
    handover: p.handover,
    keyTasks: [],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: p.category,
    contentCode: p.contentCode,
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: p.duration,
    averageScore: 0,
    platform: p.platform,
    checklist: [],
    resultMetadata,
  } as ScenarioDetail;
}

/** old_scenario_data: 재난 중증도분류 (mode_key·인계·학습목표) */
const TRIAGE_SHEET: Record<number, { handover: string; objectives: string[] }> = {
  2300: {
    handover: '매 플레이마다 고정된 10명의 환자가 제시됩니다.교차로에서 버스 간 충돌로 다수의 사상자가 발생했습니다. 대피 시작 5분 후 소방차와 앰뷸런스가 도착했으며, 구조된 피해자들은 인도와 인근 광장에 모여있습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 Global Sorting 1단계를 수행하여 전체 환자군을 신속하게 일차 분류할 수 있다.',
      '개별 환자의 호흡·순환·의식 상태를 사정하고, Global Sorting+START 분류법에 따라 환자를 긴급·응급·비응급·지연으로 분류할 수 있다.',
      '분류 결과와 해설을 바탕으로 자신의 분류 오류를 확인하고, 분류 정확도를 향상시킬 수 있다.',
    ],
  },
  2301: {
    handover: '매 플레이마다 10명의 환자가 랜덤하게 제시됩니다.교차로에서 버스 간 충돌로 다수의 사상자가 발생했습니다. 대피 시작 5분 후 소방차와 앰뷸런스가 도착했으며, 구조된 피해자들은 인도와 인근 광장에 모여있습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 Global Sorting 1단계를 수행하여 전체 환자군을 신속하게 일차 분류할 수 있다.',
      '개별 환자의 호흡·순환·의식 상태를 사정하고, Global Sorting+START 분류법에 따라 환자를 긴급·응급·비응급·지연으로 분류할 수 있다.',
      '분류 결과에 따라 처치 및 이송 우선순위를 결정하고, 자원 배분 효율성을 평가할 수 있다.',
    ],
  },
  2302: {
    handover: '매 플레이마다 8명의 환자가 랜덤하게 제시됩니다.교차로에서 버스 간 충돌로 다수의 사상자가 발생했습니다. 대피 시작 5분 후 소방차와 앰뷸런스가 도착했으며, 구조된 피해자들은 인도와 인근 광장에 모여있습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 환자의 호흡·순환·의식 상태를 START 분류 기준에 따라 신속하게 사정할 수 있다.',
      'START 분류 지침에 근거하여 환자를 긴급·응급·비응급·지연으로 분류할 수 있다.',
      '분류 결과에 따라 처치 및 이송 우선순위를 결정하고, 자원 배분 효율성을 평가할 수 있다.',
    ],
  },
  2303: {
    handover: '매 플레이마다 10명의 환자가 랜덤하게 제시됩니다.물류창고에 화재가 발생하여 다수의 사상자가 발생했습니다. 대피 시작 10분 후 소방차와 앰뷸런스가 도착했고, 구조된 피해자들은 화단 근처에 모여있습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 Global Sorting 1단계를 수행하여 전체 환자군을 신속하게 일차 분류할 수 있다.',
      '개별 환자의 호흡·순환·의식 상태를 사정하고, Global Sorting+START 분류법에 따라 환자를 긴급·응급·비응급·지연으로 분류할 수 있다.',
      '분류 결과에 따라 처치 및 이송 우선순위를 결정하고, 자원 배분 효율성을 평가할 수 있다.',
    ],
  },
  2304: {
    handover: '매 플레이마다 8명의 환자가 랜덤하게 제시됩니다.물류창고에 화재가 발생하여 다수의 사상자가 발생했습니다. 대피 시작 10분 후 소방차와 앰뷸런스가 도착했고, 구조된 피해자들은 화단 근처에 모여있습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 환자의 호흡·순환·의식 상태를 START 분류 기준에 따라 신속하게 사정할 수 있다.',
      'START 분류 지침에 근거하여 환자를 긴급·응급·비응급·지연으로 분류할 수 있다.',
      '분류 결과에 따라 처치 및 이송 우선순위를 결정하고, 자원 배분 효율성을 평가할 수 있다.',
    ],
  },
  2305: {
    handover: '매 플레이마다 10명의 환자가 랜덤하게 제시됩니다.요양원에 화재가 발생하여 다수의 사상자가 발생했습니다. 대피 시작 10분 후 소방차와 앰뷸런스가 도착했고, 구조된 피해자들은 인근 주차장 근처에 모여있습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 Global Sorting 1단계를 수행하여 전체 환자군을 신속하게 일차 분류할 수 있다.',
      '개별 환자의 호흡·순환·의식 상태를 사정하고, Global Sorting+START 분류법에 따라 환자를 긴급·응급·비응급·지연으로 분류할 수 있다.',
      '분류 결과에 따라 처치 및 이송 우선순위를 결정하고, 자원 배분 효율성을 평가할 수 있다.',
    ],
  },
  2306: {
    handover: '매 플레이마다 8명의 환자가 랜덤하게 제시됩니다.요양원에 화재가 발생하여 다수의 사상자가 발생했습니다. 대피 시작 10분 후 소방차와 앰뷸런스가 도착했고, 구조된 피해자들은 인근 주차장 근처에 모여있습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 환자의 호흡·순환·의식 상태를 START 분류 기준에 따라 신속하게 사정할 수 있다.',
      'START 분류 지침에 근거하여 환자를 긴급·응급·비응급·지연으로 분류할 수 있다.',
      '분류 결과에 따라 처치 및 이송 우선순위를 결정하고, 자원 배분 효율성을 평가할 수 있다.',
    ],
  },
};

const RAD_SHEET: Record<number, { handover: string; objectives: string[] }> = {
  2401: {
    handover:
      '매 플레이마다 고정된 5명의 환자가 제시됩니다. 경기도 내 모 콘서트장에서 폭탄 테러가 발생해 2,000명 이상의 사상자가 발생했습니다. 현장 보고에 따르면 방사선 계측기 알람이 울려, 방사능 폭탄 테러 대응 상황으로 전환되었습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 Global Sorting 1단계를 수행하여 전체 환자군을 신속하게 일차 분류하고, 해당 지역의 공간선량을 확인할 수 있다.',
      '개별 환자의 주호소·호흡·순환·의식 상태를 사정하고, 오염 계측(cps)과 급성 방사선 피폭 증후군(ARS) 발현 시간을 종합하여 Global Sorting+START 분류법에 따라 외상 중증도를 분류할 수 있다.',
      '외상 중증도 분류 결과에 방사선 피폭 상태를 반영하여 복합 중증도를 최종 분류하고, 처치 및 이송 우선순위를 결정할 수 있다.',
    ],
  },
  2402: {
    handover:
      '매 플레이마다 10명의 환자가 랜덤하게 제시됩니다. 경기도 내 모 콘서트장에서 폭탄 테러가 발생해 2,000명 이상의 사상자가 발생했습니다. 현장 보고에 따르면 방사선 계측기 알람이 울려, 방사능 폭탄 테러 대응 상황으로 전환되었습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 Global Sorting 1단계를 수행하여 전체 환자군을 신속하게 일차 분류하고, 해당 지역의 공간선량을 확인할 수 있다.',
      '개별 환자의 주호소·호흡·순환·의식 상태를 사정하고, 오염 계측(cps)과 급성 방사선 피폭 증후군(ARS) 발현 시간을 종합하여 Global Sorting+START 분류법에 따라 외상 중증도를 분류할 수 있다.',
      '외상 중증도 분류 결과에 방사선 피폭 상태를 반영하여 복합 중증도를 최종 분류하고, 처치 및 이송 우선순위를 결정할 수 있다.',
    ],
  },
  2403: {
    handover:
      '매 플레이마다 10명의 환자가 랜덤하게 제시됩니다. 경기도 내 모 콘서트장에서 폭탄 테러가 발생해 2,000명 이상의 사상자가 발생했습니다. 현장 보고에 따르면 방사선 계측기 알람이 울려, 방사능 폭탄 테러 대응 상황으로 전환되었습니다. 문제는 무작위로 출제되고 제한 시간이 적용됩니다. 평가 데이터는 교수자만 확인할 수 있습니다.',
    objectives: [
      '다수 사상자 발생 현장에서 Global Sorting 1단계를 수행하여 전체 환자군을 신속하게 일차 분류하고, 해당 지역의 공간선량을 확인할 수 있다.',
      '개별 환자의 주호소·호흡·순환·의식 상태를 사정하고, 오염 계측(cps)과 급성 방사선 피폭 증후군(ARS) 발현 시간을 종합하여 Global Sorting+START 분류법에 따라 외상 중증도를 분류할 수 있다.',
      '외상 중증도 분류 결과에 방사선 피폭 상태를 반영하여 복합 중증도를 최종 분류하고, 처치 및 이송 우선순위를 결정할 수 있다.',
    ],
  },
};

/** PPE(content_code=ppe) 수행결과 — ProcedureResult 테이블용 목 데이터 */
type PpePerformanceRow = {
  no?: number;
  evaluationItem?: string;
  prescribed?: string;
  userSelection?: string;
  result?: string;
  score?: string | number;
  comment?: string;
};

function withPpePerformance(detail: ScenarioDetail, ppePerformance: PpePerformanceRow[]): ScenarioDetail {
  const rm = (detail.resultMetadata ?? {}) as Record<string, unknown>;
  return {
    ...detail,
    resultMetadata: {
      ...rm,
      ppePerformance,
    },
  } as ScenarioDetail;
}

const PPE_PERF_BASIC_DON: PpePerformanceRow[] = [
  {
    no: 1,
    evaluationItem: '손위생(착의 전)',
    prescribed: '흐르는 물·비누 20초 이상 또는 알코올 손소독',
    userSelection: '흐르는 물·비누 20초 이상',
    result: '정답',
    score: '10점',
    comment: '',
  },
  {
    no: 2,
    evaluationItem: '속장갑 → 가운',
    prescribed: '속장갑 착용 후 가운 착용',
    userSelection: '가운 착용 후 속장갑',
    result: '오답',
    score: '0점',
    comment: '속장갑을 가운보다 먼저 착용하세요.',
  },
  {
    no: 3,
    evaluationItem: '마스크·안면보호구·겉장갑',
    prescribed: '표준 착의 순서 준수',
    userSelection: '표준 착의 순서 준수',
    result: '정답',
    score: '15점',
    comment: '',
  },
];

const PPE_PERF_BASIC_DOFF: PpePerformanceRow[] = [
  {
    no: 1,
    evaluationItem: '겉장갑 탈제',
    prescribed: '안쪽을 잡아 말아 내려 제거(오염면 비접촉)',
    userSelection: '바깥면을 잡고 제거',
    result: '오답',
    score: '0점',
    comment: '오염된 겉면을 만지지 않도록 탈제하세요.',
  },
  {
    no: 2,
    evaluationItem: '손위생',
    prescribed: '겉장갑 탈제 직후 손위생',
    userSelection: '겉장갑 탈제 직후 손위생',
    result: '정답',
    score: '10점',
    comment: '',
  },
  {
    no: 3,
    evaluationItem: '가운·속장갑 탈의 순서',
    prescribed: '비닐가운·겉장갑 → 가운·마스크 등 표준 순서',
    userSelection: '비닐가운·겉장갑 → 가운·마스크 등 표준 순서',
    result: '정답',
    score: '15점',
    comment: '',
  },
];

const PPE_PERF_LEVELD_DON: PpePerformanceRow[] = [
  {
    no: 1,
    evaluationItem: '덧신·보호복 착순',
    prescribed: '덧신 착용 후 보호복 하의·상의 순',
    userSelection: '덧신 착용 후 보호복 하의·상의 순',
    result: '정답',
    score: '12점',
    comment: '',
  },
  {
    no: 2,
    evaluationItem: '엄지고리·덧신 매듭',
    prescribed: '제조사 지침에 따른 고정',
    userSelection: '생략',
    result: '오답',
    score: '0점',
    comment: '엄지고리와 덧신 매듭을 확인하세요.',
  },
];

const PPE_PERF_LEVELD_DOFF: PpePerformanceRow[] = [
  {
    no: 1,
    evaluationItem: '표면 오염 제거',
    prescribed: '탈의 전 보호복 표면 제거·소독',
    userSelection: '탈의 전 보호복 표면 제거·소독',
    result: '정답',
    score: '10점',
    comment: '',
  },
  {
    no: 2,
    evaluationItem: '덧신 매듭 풀기 → 보호복 탈의',
    prescribed: '표준 탈의 순서',
    userSelection: '보호복만 먼저 탈의',
    result: '오답',
    score: '0점',
    comment: '덧신 매듭을 먼저 풀고 순서를 지키세요.',
  },
];

const CURRICULUM_SCENARIO_DETAILS_MORE: Record<number, ScenarioDetail> = {
  2701: withPpePerformance(
    procedureScenario({
    id: 2701,
    classId: '006',
    title: '기본보호구 착의',
    contentCode: 'ppe',
    category: 'new-nurse',
    platform: 'Mobile',
    duration: '8분',
    handover: '의료기관에서 환자를 대상으로 하는 모든 처치에 가장 기본적으로 적용하는 표준주의(Standard Precaution) 지침에 따라 개인보호장비들을 착용해보는 과정입니다. 기본보호구 착의를 실습하세요.',
    learningObjectives: [
      '기본보호구 착의에 필요한 물품(속장갑, 가운, 마스크, 안면보호구, 헤어캡, 비닐가운, 겉장갑 등)을 정확하게 선택할 수 있다.',
      '손위생을 포함한 표준 착의 순서에 따라 각 보호구를 올바르게 착용할 수 있다.',
      '결과 확인을 통해 착의 순서와 물품 선택의 오류를 파악하고, 정확도를 향상시킬 수 있다.',
    ],
    }),
    PPE_PERF_BASIC_DON,
  ),
  2702: withPpePerformance(
    procedureScenario({
    id: 2702,
    classId: '006',
    title: '기본보호구 탈의',
    contentCode: 'ppe',
    category: 'new-nurse',
    platform: 'Mobile',
    duration: '6분',
    handover: '의료기관에서 환자를 대상으로 하는 모든 처치에 기본적으로 적용하는 표준주의(Standard Precaution) 지침에 따라 개인보호구를 안전하게 제거하는 과정입니다. 기본보호구 탈의를 실습하세요.',
    learningObjectives: [
      '교차 오염을 최소화하기 위해 비닐가운·겉장갑부터 속장갑까지의 표준 탈의 순서를 파악할 수 있다.',
      '단계별 손위생을 포함한 표준 탈의 절차에 따라 각 보호구를 올바른 순서로 제거할 수 있다.',
      '결과 확인을 통해 탈의 순서의 오류를 파악하고, 정확도를 향상시킬 수 있다.',
    ],
    }),
    PPE_PERF_BASIC_DOFF,
  ),
  2703: withPpePerformance(
    procedureScenario({
    id: 2703,
    classId: '006',
    title: 'Level D 보호복 착의',
    contentCode: 'ppe',
    category: 'new-nurse',
    platform: 'Mobile',
    duration: '10분',
    handover: '고위험성 병원체 노출 환경에서 감염을 방지하기 위한 Level D 보호복을 착용하는 과정입니다. Level D 보호복 착의를 실습하세요.',
    learningObjectives: [
      'Level D 보호복 착의에 필요한 물품(속장갑, 보호복, 덧신, 마스크, 안면보호구, 겉장갑 등)을 정확하게 선택할 수 있다.',
      '손위생을 포함한 표준 착의 순서에 따라 엄지고리 끼기, 덧신 매듭 묶기 등 특수 동작을 포함한 각 보호구를 올바르게 착용할 수 있다.',
      '결과 확인을 통해 착의 순서와 물품 선택의 오류를 파악하고, 정확도를 향상시킬 수 있다.',
    ],
    }),
    PPE_PERF_LEVELD_DON,
  ),
  2704: withPpePerformance(
    procedureScenario({
    id: 2704,
    classId: '006',
    title: 'Level D 보호복 탈의',
    contentCode: 'ppe',
    category: 'new-nurse',
    platform: 'Mobile',
    duration: '8분',
    handover: '고위험성 병원체 노출 환경에서 감염을 방지하기 위한 Level D 보호복을 안전하게 제거하는 과정입니다. Level D 보호복 탈의를 실습하세요.',
    learningObjectives: [
      '탈의 전 보호복 표면의 오염물질을 확인하고 제거하며, 교차 오염을 최소화하기 위한 표준 탈의 순서를 파악할 수 있다.',
      '단계별 손위생을 포함한 표준 탈의 절차에 따라 덧신 매듭 풀기, 보호복 탈의 등 각 보호구를 올바른 순서로 제거할 수 있다.',
      '결과 확인을 통해 탈의 순서의 오류를 파악하고, 정확도를 향상시킬 수 있다.',
    ],
    }),
    PPE_PERF_LEVELD_DOFF,
  ),
  2802: procedureScenario({
    id: 2802,
    classId: '007',
    title: '호흡곤란 환자 대응 실습',
    contentCode: 'covid19',
    category: 'disaster-emergency',
    platform: 'Mobile',
    duration: '10분',
    handover: '67세 남자 환자로 내원 3일 전부터 인후통, 마른 기침과 고열, 호흡곤란이 지속되어 응급실 통해 입원하여 COVID-19 진단 받은 환자에게 격리지침에 따른 적절한 간호를 수행해 보세요.',
    learningObjectives: [
      '호흡곤란 환자의 폐음 청진과 활력징후 사정을 통해 저산소증 상태를 파악할 수 있다.',
      '수집된 사정 자료를 분석하여 호흡 부전의 위급성을 판단하고, 치료 우선순위에 따라 다학제 팀과 의사소통할 수 있다.',
      '고유량 비강캐뉼라 적용 및 비인두 흡인을 수행하고, 인퓨전 펌프를 이용한 정맥 영양(TPN)을 투여할 수 있다.',
    ],
  }),
  2803: procedureScenario({
    id: 2803,
    classId: '007',
    title: '중증환자 과정 실습',
    contentCode: 'covid19',
    category: 'disaster-emergency',
    platform: 'Mobile',
    duration: '15분',
    handover: '78세 여자 환자로 COVID-19로 악화되어 대학병원 전원 후, Ventilator 적용하여 유지 중인 환자에게 격리지침에 따른 적절한 간호를 수행해 보세요.',
    learningObjectives: [
      '인공호흡기 적용 환자의 ABGA 결과와 생리학적 상태를 모니터링하고, FLACC 도구로 통증 수준을 사정할 수 있다.',
      '인공호흡기 모니터링 자료를 종합하여 FiO2 설정 변경 및 진정제 용량 조절의 필요성을 판단하고 보고할 수 있다.',
      '인공호흡기 설정 변경과 폐쇄적 흡인을 수행하고, 항바이러스제 투약 및 신체 보호대 간호를 제공할 수 있다.',
    ],
  }),
  2902: procedureScenario({
    id: 2902,
    classId: '008',
    title: '시나리오 1 응용실습',
    contentCode: 'acls',
    category: 'disaster-emergency',
    platform: 'Mobile',
    duration: '15분',
    handover: '진행단계를 알려주는 가이드 없이 각 스테이지마다 시간이 제한됩니다.3VD로 인해 CABG 시행한 과거력 있는 환자로 3일 전 UTI에 의한 Sepsis로 ER 통해 입원하여 치료중입니다. 금일 아침 갑작스러운 흉통 호소로 모니터 위해 치료실로 이동한 환자의 상태를 사정하고 적절한 간호를 제공하세요.',
    learningObjectives: [
      '반응 없는 환자의 의식·맥박·호흡을 사정하고, DNR 확인 및 전문소생술 팀을 호출하여 역할을 분배할 수 있다.',
      '심전도 리듬(심실세동·무맥성 심실빈맥)을 확인하고, 알고리즘에 따라 가슴압박과 인공호흡, 리듬 확인, 제세동을 수행할 수 있다.',
      '처방된 약물(에피네프린·아미오다론)을 정확히 투여하고, ROSC 후 소생 후 처치를 수행할 수 있다.',
    ],
  }),
  2903: procedureScenario({
    id: 2903,
    classId: '008',
    title: '시나리오 2 기본실습',
    contentCode: 'acls',
    category: 'disaster-emergency',
    platform: 'Mobile',
    duration: '15분',
    handover: '진행단계에 모든 가이드가 제공됩니다.RCC 진단하에 Pain control 위해 입원한 환자입니다. 입원 중 발생한 High fever로 Blood culture 시행 후 항생제 치료 중 Cardiac arrest 발생한 환자에게 초기 심폐소생술을 수행하고 적절한 간호를 제공하세요.',
    learningObjectives: [
      '반응 없는 환자의 의식·맥박·호흡을 사정하고, DNR 확인 및 전문소생술 팀을 호출하여 역할을 분배할 수 있다.',
      '충격불필요리듬(무맥성 전기활동·무수축)을 확인하고, 제세동 없이 즉시 가슴압박과 인공호흡, 리듬 확인을 수행할 수 있다.',
      '처방된 약물(에피네프린)을 조기 투여하고, ROSC 후 원인 해결을 위한 바늘 흉강감압술 등의 간호 중재를 수행할 수 있다.',
    ],
  }),
  2904: procedureScenario({
    id: 2904,
    classId: '008',
    title: '시나리오 2 응용실습',
    contentCode: 'acls',
    category: 'disaster-emergency',
    platform: 'Mobile',
    duration: '15분',
    handover: '진행단계를 알려주는 가이드 없이 각 스테이지마다 시간이 제한됩니다.RCC 진단하에 Pain control 위해 입원한 환자입니다. 입원 중 발생한 High fever로 Blood culture 시행 후 항생제 치료 중 Cardiac arrest 발생한 환자에게 초기 심폐소생술을 수행하고 적절한 간호를 제공하세요.',
    learningObjectives: [
      '반응 없는 환자의 의식·맥박·호흡을 사정하고, DNR 확인 및 전문소생술 팀을 호출하여 역할을 분배할 수 있다.',
      '충격불필요리듬(무맥성 전기활동·무수축)을 확인하고, 제세동 없이 즉시 가슴압박과 인공호흡, 리듬 확인을 수행할 수 있다.',
      '처방된 약물(에피네프린)을 조기 투여하고, ROSC 후 원인 해결을 위한 바늘 흉강감압술 등의 간호 중재를 수행할 수 있다.',
    ],
  }),
};

/** old_scenario_data 나머지 ID(핵심간호술기 ls*, 질환별, 증상별) — 제목·content_code는 시트와 동일 */
function sheetRemainder(
  id: number,
  classId: string,
  title: string,
  contentCode: string,
  category: string,
  platform: 'VR' | 'Mobile' | 'PC',
  duration: string,
): ScenarioDetail {
  return procedureScenario({
    id,
    classId,
    title,
    contentCode,
    category,
    platform,
    duration,
    handover: `old_scenario_data 시트 기준: ${title}. 인계·학습목표는 시트를 참고하세요.`,
    learningObjectives: ['old_scenario_data 시트의 학습목표에 따라 실습을 완료할 수 있다.'],
  });
}

/** old_scenario_data 한 행 — 시나리오 상세·목업 커리큘럼·content_code를 이 형태로만 추가·수정 */
export interface CurriculumSheetRemainderRow {
  id: number;
  classId: string;
  title: string;
  contentCode: string;
  category: string;
  platform: 'VR' | 'Mobile' | 'PC';
  duration: string;
}

function remainderRowsToScenarioRecord(rows: readonly CurriculumSheetRemainderRow[]): Record<number, ScenarioDetail> {
  const out: Record<number, ScenarioDetail> = {};
  for (const r of rows) {
    out[r.id] = sheetRemainder(r.id, r.classId, r.title, r.contentCode, r.category, r.platform, r.duration);
  }
  return out;
}

/** 증상별 의사결정(class 011) — EvaluationTable용 목업: `stats`·`evaluationSummary`·`checklist` */
function symptomDecisionEvalMeta(duration: string, contentCode: string): Record<string, unknown> {
  const durMatch = duration.match(/[\d.]+/);
  const durDisplay = durMatch?.[0] ?? '—';
  return {
    stats: [
      { label: '정답율', value: '85', sub: '%', color: 'text-green-600' },
      { label: '정답수/전체', value: '17', sub: '/ 20', color: 'text-gray-600' },
      { label: '소요시간', value: durDisplay, sub: duration.includes('분') ? '분' : '', color: 'text-gray-600' },
    ],
    evaluationSummary: {
      score: 85,
      scoreSub: '%',
      correctCount: 17,
      total: 20,
      duration,
    },
    contentCode,
  };
}

function symptomDecisionDemoChecklist(contentCode: string, title: string): ChecklistItem[] {
  const id = (n: number) => `${contentCode.replace(/[^a-zA-Z0-9_]/g, '_')}_chk_${n}`;
  return [
    {
      id: id(1),
      order: 1,
      evaluationType: 'performance',
      eventName: '증상 사정',
      taskType: 'To-do',
      taskName: `${title}: S·O 자료 수집`,
      description: '주증상·발병 경과, 동반 증상, 과거력·복용약, 알레르기를 정리합니다.',
      score: 5,
      feedback: '객관적 자료(활력·의식)와 연계하면 중증도 판단이 명확해집니다.',
      status: 'success',
    },
    {
      id: id(2),
      order: 2,
      evaluationType: 'performance',
      eventName: '모니터링',
      taskType: 'To-do',
      taskName: '활력징후·위험 징후 재평가',
      description: '초기 Vital을 측정하고, 악화 시 알람 기준을 적용합니다.',
      score: 5,
      feedback: '간격을 정해 재측정한 시각을 기록하면 후속 의료진과 공유가 쉽습니다.',
      status: 'partial',
    },
    {
      id: id(3),
      order: 3,
      evaluationType: 'performance',
      eventName: '간호 중재',
      taskType: 'Decision',
      taskName: '우선순위 간호중재 선택',
      description: '환자 상태에 맞는 기본 중재(예: 기도·산소·채혈·안전)를 선택합니다.',
      score: 10,
      feedback: '중재 전·후 환자 반응을 짧게라도 기록하세요.',
      status: 'success',
    },
    {
      id: id(4),
      order: 4,
      evaluationType: 'performance',
      eventName: '의사소통',
      taskType: 'Decision',
      taskName: 'SBAR 형식 보고',
      description: 'Situation·Background·Assessment·Recommendation 순으로 핵심만 전달합니다.',
      score: 8,
      feedback: 'Assessment에는 자신의 임상 추론과 요청 사항을 명확히 담습니다.',
      status: 'partial',
    },
    {
      id: id(5),
      order: 5,
      evaluationType: 'performance',
      eventName: '환자 안전',
      taskType: 'Must-not',
      taskName: '위험 처방·술기 오류 회피',
      description: '검증되지 않은 경로의 약물 투여 또는 금기 처방을 그대로 수행하지 않습니다.',
      score: 15,
      feedback: '이상 시 처방·교차확인 및 Charge 보고 절차를 준수했습니다.',
      status: 'success',
    },
  ];
}

function symptomDecisionRowsToScenarioRecord(rows: readonly CurriculumSheetRemainderRow[]): Record<number, ScenarioDetail> {
  const out: Record<number, ScenarioDetail> = {};
  for (const r of rows) {
    out[r.id] = procedureScenario({
      id: r.id,
      classId: r.classId,
      title: r.title,
      contentCode: r.contentCode,
      category: r.category,
      platform: r.platform,
      duration: r.duration,
      handover: `old_scenario_data 시트 기준: ${r.title}. 증상에 따른 사정·SBAR 보고·간호중재 의사결정 실습입니다. 상세 인계는 시트를 참고하세요.`,
      learningObjectives: [
        '주증상과 동반 증상을 체계적으로 사정하고 위험 신호를 식별할 수 있다.',
        'SBAR로 핵심 정보를 전달하고 의료진과 협력할 수 있다.',
        '환자 상태에 맞는 우선순위 간호중재를 선택·수행할 수 있다.',
      ],
      resultMetadata: {
        ...symptomDecisionEvalMeta(r.duration, r.contentCode),
        checklist: symptomDecisionDemoChecklist(r.contentCode, r.title),
      },
    });
  }
  return out;
}

/** old_scenario_data: 핵심간호술기(class 009) — 시나리오별 인계·학습목표 */
const CORE_NURSING_SHEET: Record<number, { handover: string; objectives: string[] }> = {
  3001: {
    handover:
      '경구약 처방이 확인된 입원 환자입니다. 5대 원칙(정확한 환자·약물·용량·경로·시간)에 따라 경구투약을 안전하게 수행하고, 환자 교육 및 이상 반응 관찰 포인트를 적용하세요.',
    objectives: [
      '환자 신원·처방·알레르기 이력을 확인하고, 투약 전·후 평가 항목을 점검할 수 있다.',
      '약물 준비·전달·복용 확인 절차를 표준에 맞게 수행할 수 있다.',
      '투약 기록과 환자·보호자 교육 내용을 문서화할 수 있다.',
    ],
  },
  3002: {
    handover:
      '저산소증 소견이 있는 환자에게 의사 처방에 따라 비강 캐뉼라를 이용한 산소요법이 필요합니다. 유량 설정·장치 고정·피부·점막 상태를 점검하며 안전하게 시행하세요.',
    objectives: [
      '산소요법 적응·금기와 처방 유량을 확인하고, 캐뉼라 종류·크기를 선택할 수 있다.',
      '가습·고정·자세 등 캐뉼라 적용 절차를 올바르게 수행할 수 있다.',
      '산소포화도 모니터링과 이상 시 중재·보고 기준을 설명할 수 있다.',
    ],
  },
  3003: {
    handover:
      '말초산소포화도 측정과 심전도 모니터 연결이 필요한 환자입니다. 센서 부착·파형·알람 설정을 확인하고, 간호 기록에 반영할 수 있도록 측정값을 해석하세요.',
    objectives: [
      'SpO₂ 측정 시 부착 부위·움직임·저관류 등 간섭 요인을 고려해 측정할 수 있다.',
      '리드 배치·피부 준비·파형 품질을 점검하며 심전도 모니터를 적용할 수 있다.',
      '측정값·파형 이상 시 즉각적 조치와 보고를 할 수 있다.',
    ],
  },
  3004: {
    handover:
      '변비·장 폐색 위험이 있는 환자에게 배출관장이 처방되었습니다. 금기·용액·온도·깊이·속도를 준수하고, 환자 불편·합병증 징후를 관찰하세요.',
    objectives: [
      '관장 적응·금기와 처방(용액·양·온도)을 확인할 수 있다.',
      '무균·사생활 보호 원칙에 따라 관장 절차를 단계별로 수행할 수 있다.',
      '시행 후 배변·복부팽만·전해질 이상 등을 평가하고 기록할 수 있다.',
    ],
  },
  3005: {
    handover:
      '정기 활력징후 측정이 필요한 병동 환자입니다. 혈압·맥박·호흡·체온을 표준 절차로 측정하고, 이상 소견 시 재측정·보고 기준을 적용하세요.',
    objectives: [
      '측정 전 환자 안정·자세·팔·커프 크기 등 측정 조건을 점검할 수 있다.',
      '혈압·맥박·호흡·체온을 정확히 측정하고 정상·이상 범위를 구별할 수 있다.',
      '간호 기록에 측정 시각·상황·중재를 명확히 문서화할 수 있다.',
    ],
  },
  3006: {
    handover:
      '욕창 위험도가 높고 낙상 위험이 동반된 환자입니다. 압력 재분배·피부 사정·낙상 예방 중재를 통합하여 안전한 간호를 제공하세요.',
    objectives: [
      '욕창 위험도 도구와 피부·골돌기 부위를 체계적으로 사정할 수 있다.',
      '체위변경·쿠션·교육 등 욕창 예방 중재를 계획·실행할 수 있다.',
      '낙상 위험 요인을 식별하고 환경·행동 중재를 적용할 수 있다.',
    ],
  },
  3007: {
    handover:
      '근육주사가 처방된 환자입니다. 주사 부위·각도·깊이·무균술을 준수하고, 주사 후 관찰·기록을 수행하세요.',
    objectives: [
      '처방·약물·용량·경로를 확인하고 적절한 주사 부위를 선택할 수 있다.',
      '드로우백·주입 속도 등을 포함한 근육주사 표준 절차를 수행할 수 있다.',
      '국소 반응·전신 이상 반응을 관찰하고 문서화할 수 있다.',
    ],
  },
  3008: {
    handover:
      '피하주사와 간이 혈당측정이 필요한 환자입니다. 주사 부위 로테이션·혈당 시료 채취·결과 해석을 통합하여 시행하세요.',
    objectives: [
      '피하주사 부위 선정·교체와 무균 절차를 적용할 수 있다.',
      '혈당측정기 사용·시료 채취·교정 여부를 점검할 수 있다.',
      '혈당 결과에 따른 보고·교육 포인트를 설명할 수 있다.',
    ],
  },
  3009: {
    handover:
      '정맥 수액 주입이 처방된 환자입니다. 정맥로 확보·연결·속도 조절·침습 관련 감염 예방을 준수하세요.',
    objectives: [
      '처방(약물·용량·속도)과 정맥로 상태를 확인할 수 있다.',
      '무균 연결·에어 제거·속도 설정·라벨링을 올바르게 수행할 수 있다.',
      '침입 부위·침투·전신 이상 반응을 관찰하고 기록할 수 있다.',
    ],
  },
  3010: {
    handover:
      '중등도 이상 통증을 호소하는 환자입니다. 통증 평가 도구를 사용해 강도·특성을 사정하고, 비약물·약물 중재 계획에 따라 간호를 제공하세요.',
    objectives: [
      '통증 평가 척도를 선택·적용하고 재평가 시점을 계획할 수 있다.',
      '비약물적 통증 완화 중재를 적용할 수 있다.',
      '통증 완화 효과·부작용을 관찰하고 다학제적 보고를 할 수 있다.',
    ],
  },
  3011: {
    handover:
      '전완 내측면 피내주사가 필요한 환자입니다(예: 결핵·알레르기 검사). 부위·각도·양·판독 시점을 준수하세요.',
    objectives: [
      '피내주사 적응·금기와 처방을 확인할 수 있다.',
      '전완 내측면 등 표준 부위에 정확한 각도·양으로 투여할 수 있다.',
      '국소 반응 관찰·환자 교육·기록을 수행할 수 있다.',
    ],
  },
  3012: {
    handover:
      '기도 분비물이 많아 흡인이 필요한 환자입니다. 무균·산소포화도 모니터링·흡인 시간·깊이를 고려해 안전하게 시행하세요.',
    objectives: [
      '흡인 적응·금기와 장비 준비·환자 준비를 점검할 수 있다.',
      '구강·비인두 흡인 절차를 표준에 맞게 수행할 수 있다.',
      '산소포화도·호흡음 변화를 관찰하고 합병증 징후에 대응할 수 있다.',
    ],
  },
  3013: {
    handover:
      'JP 또는 Hemovac 배액관이 유치된 수술 후 환자입니다. 배액량·색·특성을 관찰하고, 음압·밀봉·교육을 포함한 관리를 수행하세요.',
    objectives: [
      '배액관 종류·작동 원리와 관찰 항목을 설명할 수 있다.',
      '배액 측정·기록·밀봉 상태 점검을 올바르게 수행할 수 있다.',
      '이상 배액·탈락·감염 징후 시 보고·중재를 할 수 있다.',
    ],
  },
  3014: {
    handover:
      '수혈이 처방된 환자입니다. 환자·혈액 확인 이중 점검, 속도 조절, 이상 반응 모니터링을 엄격히 준수하세요.',
    objectives: [
      '수혈 전 환자 확인·동의·Vital·처방·혈액 라벨 검증을 수행할 수 있다.',
      '초기 속도·관찰 간격을 준수하며 수혈을 시작·유지할 수 있다.',
      '급성·지연성 수혈 반응을 식별하고 즉각 중단·보고·처치를 할 수 있다.',
    ],
  },
  3015: {
    handover:
      '간헐적 위관영양이 필요한 환자입니다. 관 위치·잔량·주입 속도·자세를 확인하고, 오심·구토·흡인 위험을 최소화하세요.',
    objectives: [
      '위관영양 적응·금기와 처방(양·속도·농도)을 확인할 수 있다.',
      '관 위치·위 잔량 확인 등 주입 전 점검을 수행할 수 있다.',
      '주입 중·후 불내성 징후를 관찰하고 기록할 수 있다.',
    ],
  },
  3016: {
    handover:
      '심정지가 의심되는 상황입니다. 반응·호흡·맥박을 신속히 확인하고, 기본 심폐소생술과 제세동기 사용 알고리즘에 따라 대응하세요.',
    objectives: [
      '심정지 인지·반응 확인·도움 요청·가슴압박 시작을 신속히 수행할 수 있다.',
      '가슴압박 깊이·속도·완전 이완과 인공호흡 비율을 유지할 수 있다.',
      '제세동기 준비·패드 부착·리듬 분석·충격 안전을 수행할 수 있다.',
    ],
  },
  3017: {
    handover:
      '여성 환자에게 단순도뇨가 필요합니다. 무균·사생활 보호·도뇨관 크기 선택·삽입 깊이·소변량 관찰을 준수하세요.',
    objectives: [
      '도뇨 적응·금기와 필요 물품·환자 교육을 준비할 수 있다.',
      '여성 해부에 맞는 무균 단순도뇨 절차를 수행할 수 있다.',
      '삽입 후 소변 유출·통증·혈뇨를 관찰하고 기록할 수 있다.',
    ],
  },
  3018: {
    handover:
      '남성 환자에게 단순도뇨가 필요합니다. 무균·사생활 보호·전립선 비대 등 해부적 변수를 고려해 안전하게 시행하세요.',
    objectives: [
      '도뇨 적응·금기와 물품·환자 자세를 준비할 수 있다.',
      '남성 해부에 맞는 무균 단순도뇨 절차를 수행할 수 있다.',
      '저항·통증 시 중단 기준과 보고를 적용할 수 있다.',
    ],
  },
  3019: {
    handover:
      '여성 환자에게 유치도뇨가 필요합니다. 무균 삽입·기구 선택·고정·밀봉·배뇨관 관찰을 포함한 전 과정을 수행하세요.',
    objectives: [
      '유치도뇨 적응·금기와 카테터 크기 선택 근거를 설명할 수 있다.',
      '무균 유치도뇨 삽입·고정·밀봉 절차를 표준에 맞게 수행할 수 있다.',
      '배뇨량·색·통증·감염 징후를 관찰하고 문서화할 수 있다.',
    ],
  },
  3020: {
    handover:
      '남성 환자에게 유치도뇨가 필요합니다. 무균 삽입·전립선 해부·고정·관찰을 통합하여 합병증을 예방하세요.',
    objectives: [
      '유치도뇨 적응·금기와 물품·환자 설명을 준비할 수 있다.',
      '남성 환자에 대한 무균 유치도뇨 절차를 수행할 수 있다.',
      '삽입 후 배뇨·누뇨·감염 징후를 관찰하고 교육·기록을 할 수 있다.',
    ],
  },
};

/** 핵심간호술기(ls) 목업용 평가 메타 — 실 API 연동 시 동일 키 구조로 치환 */
function lsCoreNursingDemoResultMetadata(duration: string, title: string): Record<string, unknown> {
  const durMatch = duration.match(/[\d.]+/);
  const durDisplay = durMatch?.[0] ?? '—';
  return {
    stats: [
      { label: '정답율', value: '88', sub: '%', color: 'text-green-600' },
      { label: '정답수/전체', value: '22', sub: '/ 25', color: 'text-gray-600' },
      { label: '소요시간', value: durDisplay, sub: duration.includes('분') ? '분' : '', color: 'text-gray-600' },
    ],
    evaluationSummary: {
      score: 88,
      scoreSub: '%',
      correctCount: 22,
      total: 25,
      duration,
    },
    /** ProcedureResult 절차 테이블 — IV 스키마와 동일(평가항목·정답·결과·점수) */
    ivPerformance: [
      {
        no: 1,
        evaluationItem: '실습 전 환자·처방·장비 확인',
        prescribed: '신원·알레르기·처방·용기구 확인',
        userSelection: '신원·알레르기·처방·용기구 확인',
        result: '정답',
        score: '10점',
        comment: '',
      },
      {
        no: 2,
        evaluationItem: `${title} 표준 절차 수행`,
        prescribed: '단계별 무균·안전 절차',
        userSelection: '단계별 무균·안전 절차',
        result: '정답',
        score: '15점',
        comment: '',
      },
      {
        no: 3,
        evaluationItem: '중재 후 관찰·환자교육·간호기록',
        prescribed: 'Vital·이상반응 관찰 및 기록',
        userSelection: '관찰 일부 생략',
        result: '오답',
        score: '8점',
        comment: '시행 후 관찰 항목과 기록 보완이 필요합니다.',
      },
    ],
  };
}

function coreNursingRowsToScenarioRecord(rows: readonly CurriculumSheetRemainderRow[]): Record<number, ScenarioDetail> {
  const out: Record<number, ScenarioDetail> = {};
  for (const r of rows) {
    const sheet = CORE_NURSING_SHEET[r.id];
    out[r.id] = procedureScenario({
      id: r.id,
      classId: r.classId,
      title: r.title,
      contentCode: r.contentCode,
      category: r.category,
      platform: r.platform,
      duration: r.duration,
      handover:
        sheet?.handover ??
        `old_scenario_data 시트 기준: ${r.title}. 인계·학습목표는 시트를 참고하세요.`,
      learningObjectives: sheet?.objectives ?? ['old_scenario_data 시트의 학습목표에 따라 실습을 완료할 수 있다.'],
      resultMetadata: lsCoreNursingDemoResultMetadata(r.duration, r.title),
    });
  }
  return out;
}

/** 핵심간호술기(class 009) — A열 content_code·시나리오 ID·제목 일괄 관리 */
export const CORE_NURSING_CURRICULUM_SHEET_ROWS: readonly CurriculumSheetRemainderRow[] = [
  { id: 3001, classId: '009', title: '경구투약', contentCode: 'ls000001', category: 'new-nurse', platform: 'VR', duration: '5분' },
  { id: 3002, classId: '009', title: '비강 캐뉼라를 이용한 산소요법', contentCode: 'ls000002', category: 'new-nurse', platform: 'VR', duration: '8분' },
  { id: 3003, classId: '009', title: '말초산소포화도 측정과 심전도 모니터 적용', contentCode: 'ls000003', category: 'new-nurse', platform: 'VR', duration: '10분' },
  { id: 3004, classId: '009', title: '배출관장', contentCode: 'ls000004', category: 'new-nurse', platform: 'VR', duration: '7분' },
  { id: 3005, classId: '009', title: '활력징후 측정', contentCode: 'ls000005', category: 'new-nurse', platform: 'VR', duration: '7분' },
  { id: 3006, classId: '009', title: '욕창관리 및 낙상 예방 간호', contentCode: 'ls000006', category: 'new-nurse', platform: 'VR', duration: '10분' },
  { id: 3007, classId: '009', title: '근육 주사', contentCode: 'ls000007', category: 'new-nurse', platform: 'VR', duration: '8분' },
  { id: 3008, classId: '009', title: '피하주사(간이 혈당측정 검사 포함)', contentCode: 'ls000008', category: 'new-nurse', platform: 'VR', duration: '6분' },
  { id: 3009, classId: '009', title: '정맥 수액 주입', contentCode: 'ls000009', category: 'new-nurse', platform: 'VR', duration: '10분' },
  { id: 3010, classId: '009', title: '통증관리', contentCode: 'ls000010', category: 'new-nurse', platform: 'VR', duration: '8분' },
  { id: 3011, classId: '009', title: '피내주사(전완의 내측면)', contentCode: 'ls000011', category: 'new-nurse', platform: 'VR', duration: '6분' },
  { id: 3012, classId: '009', title: '흡인', contentCode: 'ls000012', category: 'new-nurse', platform: 'VR', duration: '8분' },
  { id: 3013, classId: '009', title: '배액관 관리(JP 또는 Hemovac)', contentCode: 'ls000013', category: 'new-nurse', platform: 'VR', duration: '8분' },
  { id: 3014, classId: '009', title: '수혈요법', contentCode: 'ls000014', category: 'new-nurse', platform: 'VR', duration: '10분' },
  { id: 3015, classId: '009', title: '간헐적 위관영양', contentCode: 'ls000015', category: 'new-nurse', platform: 'VR', duration: '8분' },
  { id: 3016, classId: '009', title: '기본 심폐소생술 및 제세동기 적용', contentCode: 'ls000016', category: 'new-nurse', platform: 'VR', duration: '12분' },
  { id: 3017, classId: '009', title: '단순도뇨 (여성)', contentCode: 'ls000017', category: 'new-nurse', platform: 'VR', duration: '10분' },
  { id: 3018, classId: '009', title: '단순도뇨 (남성)', contentCode: 'ls000018', category: 'new-nurse', platform: 'VR', duration: '10분' },
  { id: 3019, classId: '009', title: '유치도뇨 (여성)', contentCode: 'ls000019', category: 'new-nurse', platform: 'VR', duration: '10분' },
  { id: 3020, classId: '009', title: '유치도뇨 (남성)', contentCode: 'ls000020', category: 'new-nurse', platform: 'VR', duration: '10분' },
];

/** 질환별 환자간호(class 010) — content_code·시나리오 ID 일괄 관리 */
export const NURSING_COLLEGE_DISEASE_CURRICULUM_SHEET_ROWS: readonly CurriculumSheetRemainderRow[] = [
  { id: 3103, classId: '010', title: '분만전간호', contentCode: 'antepartumcare', category: 'nursing-college', platform: 'VR', duration: '15분' },
  { id: 3104, classId: '010', title: '분만간호', contentCode: 'intrapartumcare', category: 'nursing-college', platform: 'VR', duration: '15분' },
  { id: 3105, classId: '010', title: '식도정맥류출혈', contentCode: 'evarixbleeding', category: 'nursing-college', platform: 'VR', duration: '15분' },
  { id: 3106, classId: '010', title: '전해질불균형', contentCode: 'electrolyteimbalance', category: 'nursing-college', platform: 'VR', duration: '15분' },
  { id: 3107, classId: '010', title: '급성심근경색', contentCode: 'acutemyocardialinfarction', category: 'nursing-college', platform: 'VR', duration: '15분' },
  { id: 3108, classId: '010', title: '임신성당뇨', contentCode: 'gestationaldiabetes', category: 'nursing-college', platform: 'VR', duration: '15분' },
];

/** 증상별 의사결정(class 011) — content_code·시나리오 ID 일괄 관리 */
export const NURSING_COLLEGE_SYMPTOM_CURRICULUM_SHEET_ROWS: readonly CurriculumSheetRemainderRow[] = [
  { id: 3201, classId: '011', title: '의식변화 (응급의학과)', contentCode: 'S01_ED_Hypoglycemia', category: 'nursing-college', platform: 'VR', duration: '12분' },
  { id: 3202, classId: '011', title: '고체온 (간담췌외과)', contentCode: 'S02_ED_Highfever', category: 'nursing-college', platform: 'VR', duration: '10분' },
  { id: 3203, classId: '011', title: '저혈압 (소화기내과)', contentCode: 'S03_LowBP', category: 'nursing-college', platform: 'VR', duration: '10분' },
  { id: 3204, classId: '011', title: '흉통 (심장내과)', contentCode: 'S04_ChestPain', category: 'nursing-college', platform: 'VR', duration: '10분' },
  { id: 3205, classId: '011', title: '가슴답답함 (심장내과)', contentCode: 'S05_Arrhythmia', category: 'nursing-college', platform: 'VR', duration: '10분' },
  { id: 3206, classId: '011', title: '오심구토 (일반외과)', contentCode: 'S06_NauseaVomit', category: 'nursing-college', platform: 'VR', duration: '10분' },
  { id: 3207, classId: '011', title: '오심구토 (소화기내과)', contentCode: 'S07_GIbleeding', category: 'nursing-college', platform: 'VR', duration: '10분' },
  { id: 3208, classId: '011', title: '호흡곤란 (간담췌내과)', contentCode: 'S08_Dyspnea', category: 'nursing-college', platform: 'VR', duration: '10분' },
  { id: 3209, classId: '011', title: '고체온/호흡곤란 (소아청소년과)', contentCode: 'S09_Ped_Pneumonia', category: 'nursing-college', platform: 'VR', duration: '10분' },
  { id: 3210, classId: '011', title: '고체온 (소아청소년과)', contentCode: 'S10_Hypertension_Headache', category: 'nursing-college', platform: 'VR', duration: '10분' },
];

export const CORE_NURSING_SKILLS_SCENARIO_COUNT = CORE_NURSING_CURRICULUM_SHEET_ROWS.length;

/** 커리큘럼 순번 1기반 → content_code (핵심간호술기 행 순서와 동일해야 함) */
export function lsContentCodeFromCoreNursingOrdinal(ordinal: number): string | null {
  const row = CORE_NURSING_CURRICULUM_SHEET_ROWS[ordinal - 1];
  return row?.contentCode ?? null;
}

export function lsContentCodeFromCoreNursingScenarioId(scenarioId: number | string | null | undefined): string | null {
  if (scenarioId == null || scenarioId === '') return null;
  const n = typeof scenarioId === 'string' ? Number(scenarioId) : scenarioId;
  if (!Number.isFinite(n)) return null;
  const row = CORE_NURSING_CURRICULUM_SHEET_ROWS.find((r) => r.id === n);
  return row?.contentCode ?? null;
}

export function isCoreNursingSkillsClass(classItem: { id?: string; title?: string } | null | undefined): boolean {
  if (!classItem) return false;
  if (classItem.id === '009') return true;
  return classItem.title === '핵심간호술기';
}

export function resolveLsContentCodeForCoreNursingSession(
  classItem: {
    id?: string;
    title?: string;
    curriculum?: ReadonlyArray<{ id?: number | string; contentCode?: string }>;
  } | null | undefined,
  sessionId: string | null | undefined,
): string | null {
  if (!classItem || sessionId == null || sessionId === '') return null;
  if (!isCoreNursingSkillsClass(classItem)) return null;
  const curriculum = classItem.curriculum;
  if (curriculum?.length) {
    const idx = curriculum.findIndex((s) => String(s.id) === String(sessionId));
    if (idx >= 0) return lsContentCodeFromCoreNursingOrdinal(idx + 1);
  }
  return lsContentCodeFromCoreNursingScenarioId(sessionId);
}

/** mock `009` 커리큘럼 — 시트 테이블과 동일 소스 */
export function coreNursingCurriculumItemsForMock(): Array<{
  id: number;
  code: string;
  name: string;
  duration: string;
  platform: string;
  contentCode: string;
}> {
  return CORE_NURSING_CURRICULUM_SHEET_ROWS.map((r, i) => ({
    id: r.id,
    code: `CORE${String(i + 1).padStart(2, '0')}`,
    name: r.title,
    duration: r.duration,
    platform: r.platform,
    contentCode: r.contentCode,
  }));
}

const CURRICULUM_SHEET_REMAINING: Record<number, ScenarioDetail> = {
  ...coreNursingRowsToScenarioRecord(CORE_NURSING_CURRICULUM_SHEET_ROWS),
  ...remainderRowsToScenarioRecord(NURSING_COLLEGE_DISEASE_CURRICULUM_SHEET_ROWS),
  ...symptomDecisionRowsToScenarioRecord(NURSING_COLLEGE_SYMPTOM_CURRICULUM_SHEET_ROWS),
};

export const CURRICULUM_SCENARIO_DETAILS: Record<number, ScenarioDetail> = {
  2201: ktasScenario(
    2201,
    '응급실 내원환자',
    '10분',
    '실습 종료 후 실습기록에서 평가 데이터를 확인할 수 있습니다.다양한 주증상과 중증도를 가진 환자들이 응급실에 내원하고 있습니다. 각 환자의 임상 정보를 수집하고, KTAS 분류 기준에 따라 주증상과 중증도를 분류하세요.',
    [
      '환자의 기본 정보 수집과 필요 시 탈의를 통해 주관적·객관적 자료를 확인하고, 적절한 주증상을 의학용어로 선택할 수 있다.',
      '활력징후, 산소포화도·혈당, GCS, 근력 사정을 수행하고 모니터 결과를 해석하여 정상·이상 소견을 구별할 수 있다.',
      'KTAS 4단계 분류 기준을 단계별로 적용하여 성인·소아·모성·정신 환자군별 특성을 비교하고 최종 Level을 도출할 수 있다.',
    ],
  ),
  2202: ktasScenario(
    2202,
    '연습모드',
    '10분',
    '문제는 무작위로 출제되며, 실습 종료 후 실습기록에서 평가 데이터를 확인할 수 있습니다.다양한 주증상과 중증도를 가진 환자들이 응급실에 내원하고 있습니다. 각 환자의 임상 정보를 수집하고, KTAS 분류 기준에 따라 주증상과 중증도를 분류하세요.',
    [
      '환자의 기본 정보 수집과 필요 시 탈의를 통해 주관적·객관적 자료를 확인하고, 적절한 주증상을 의학용어로 선택할 수 있다.',
      '활력징후, 산소포화도·혈당, GCS, 근력 사정을 수행하고 모니터 결과를 해석하여 정상·이상 소견을 구별할 수 있다.',
      'KTAS 4단계 분류 기준을 단계별로 적용하여 성인·소아·모성·정신 환자군별 특성을 비교하고 최종 Level을 도출할 수 있다.',
    ],
  ),
  2203: ktasScenario(
    2203,
    '평가모드',
    '10분',
    '문제는 무작위로 출제되고 제한 시간이 적용됩니다. 평가 데이터는 교수자만 확인할 수 있습니다.다양한 주증상과 중증도를 가진 환자들이 응급실에 내원하고 있습니다. 각 환자의 임상 정보를 수집하고, KTAS 분류 기준에 따라 주증상과 중증도를 분류하세요.',
    [
      '환자의 기본 정보 수집과 필요 시 탈의를 통해 주관적·객관적 자료를 확인하고, 적절한 주증상을 의학용어로 선택할 수 있다.',
      '활력징후, 산소포화도·혈당, GCS, 근력 사정을 수행하고 모니터 결과를 해석하여 정상·이상 소견을 구별할 수 있다.',
      'KTAS 4단계 분류 기준을 단계별로 적용하여 성인·소아·모성·정신 환자군별 특성을 비교하고 최종 Level을 도출할 수 있다.',
    ],
  ),

  2300: triageScenario(2300, '버스추돌사고 연습모드', '10분', TRIAGE_SHEET[2300]),
  2301: triageScenario(2301, '버스추돌사고', '10분', TRIAGE_SHEET[2301]),
  2302: triageScenario(2302, '버스추돌사고', '10분', TRIAGE_SHEET[2302]),
  2303: triageScenario(2303, '물류창고 화재사고', '10분', TRIAGE_SHEET[2303]),
  2304: triageScenario(2304, '물류창고 화재사고', '8분', TRIAGE_SHEET[2304]),
  2305: triageScenario(2305, '요양원 화재사고', '10분', TRIAGE_SHEET[2305]),
  2306: triageScenario(2306, '요양원 화재사고', '8분', TRIAGE_SHEET[2306]),

  2401: radiationScenario(2401, '폭발 사고 (튜토리얼)', '10분', RAD_SHEET[2401]),
  2402: radiationScenario(2402, '폭발 사고 (학습모드)', '10분', RAD_SHEET[2402]),
  2403: radiationScenario(2403, '폭발 사고 (평가모드)', '10분', RAD_SHEET[2403]),
  2501: {
    id: 2501,
    title: 'GCS',
    classId: '004',
    learningObjectives: [
      '환자의 눈뜨기반응(E), 언어반응(V), 운동반응(M)을 사정하고, 필요시 부르기, 말걸기, 움직임 지시 및 통증 자극을 적절히 적용할 수 있다.',
      'GCS 각 항목의 점수를 합산하여 환자의 의식 수준 및 신경학적 상태를 정확하게 판단할 수 있다.',
      '항목별 정답 확인을 통해 자신의 사정 오류를 파악하고, GCS 평가의 정확도를 향상시킬 수 있다.',
    ],
    duration: '7분',
    supportedDevices: ['Mobile'],
    platform: 'Mobile',
    videoUrl: '',
    handover: '외과병동에 입원 중인 환자들을 대상으로 다양한 케이스를 통해 GCS 사정을 실습하세요.',
    keyTasks: ['눈뜨기 반응', '언어 반응', '운동 반응', '합계 산출'],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'new-nurse',
    contentCode: 'gcs',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: '7분',
    averageScore: 85,
    checklist: [],
    resultMetadata: {
      stats: [
        { label: '정답율', value: '92', sub: '%', color: 'text-green-600' },
        { label: '정답수/전체', value: '10', sub: '/ 12', color: 'text-blue-600' },
        { label: '소요시간', value: '12', sub: '', color: 'text-gray-600' }
      ],
      gcsPerformance: [
        { no: 1, name: '환자 A', gender: '남', age: '65세', correct_eye_response: '4', user_eye_response: '4', correct_verbal_response: '5', user_verbal_response: '5', correct_motor_response: '6', user_motor_response: '6', correct_total: '15', user_total: '15', completed_missed: 'Completed', comment: '정상 범위' },
        { no: 2, name: '환자 B', gender: '여', age: '42세', correct_eye_response: '3', user_eye_response: '3', correct_verbal_response: '4', user_verbal_response: '5', correct_motor_response: '5', user_motor_response: '5', correct_total: '12', user_total: '13', completed_missed: 'Missed', comment: '' },
        { no: 3, name: '환자 C', gender: '남', age: '58세', correct_eye_response: '2', user_eye_response: '2', correct_verbal_response: '3', user_verbal_response: '2', correct_motor_response: '4', user_motor_response: '4', correct_total: '9', user_total: '8', completed_missed: 'Missed', comment: '언어 반응 재확인 필요' }
      ]
    }
  } as ScenarioDetail,
  // 환자사정 클래스 004 - 동공반응평가
  2502: {
    id: 2502,
    title: '동공반응평가',
    classId: '004',
    learningObjectives: [
      '펜라이트를 이용하여 양쪽 동공의 크기, 모양, 대칭성 및 빛에 대한 동공 반사를 사정할 수 있다.',
      '동공 반응의 상태(Prompt, Sluggish, Fixed 등)를 분석하여 신경계 이상 징후를 신속히 판단할 수 있다.',
      '항목별 정답 확인을 통해 자신의 사정 오류를 파악하고, 동공반응평가의 정확도를 향상시킬 수 있다.',
    ],
    duration: '6분',
    supportedDevices: ['Mobile'],
    platform: 'Mobile',
    videoUrl: '',
    handover: '외과병동에 입원 중인 환자들을 대상으로 다양한 케이스를 통해 동공반응평가를 실습하세요.',
    keyTasks: ['동공 크기', '광반응', '대칭성'],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'new-nurse',
    contentCode: 'pupil_reaction',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: '6분',
    averageScore: 0,
    checklist: [],
    resultMetadata: {
      stats: [
        { label: '총점', value: '92', sub: '/ 100점', color: 'text-blue-600' },
        { label: '정답율', value: '92', sub: '%', color: 'text-green-600' },
        { label: '소요시간', value: '5', sub: '', color: 'text-gray-600' }
      ],
      gcsPerformance: [],
      pupilReactionPerformance: [
        { no: 1, name: '이보라', gender: 'F', age: '31', position: 'L', pupil_size: '3', pupil_reaction: 'Sluggish', pupil_shape: 'Ovoid', correct_pupil_reaction: 'Prompt', result: '오답', comment: '' },
        { no: 1, name: '이보라', gender: 'F', age: '31', position: 'R', pupil_size: '6', pupil_reaction: 'Prompt', pupil_shape: 'Round', result: '정답', comment: '' },
        { no: 2, name: '김철수', gender: 'M', age: '45', position: 'L', pupil_size: '4', pupil_reaction: 'Prompt', pupil_shape: 'Round', correct_pupil_reaction: 'Sluggish', result: '오답', comment: '정상 범위' },
        { no: 2, name: '김철수', gender: 'M', age: '45', position: 'R', pupil_size: '4', pupil_reaction: 'Sluggish', pupil_shape: 'Ovoid', correct_pupil_reaction: 'Prompt', result: '오답', comment: '우측 재측정 권장' },
        { no: 3, name: '박영수', gender: 'M', age: '28', position: 'L', pupil_size: '5', pupil_reaction: 'Prompt', pupil_shape: 'Round', result: '정답', comment: '' },
        { no: 3, name: '박영수', gender: 'M', age: '28', position: 'R', pupil_size: '5', pupil_reaction: 'Prompt', pupil_shape: 'Round', result: '정답', comment: '' },
      ]
    }
  } as ScenarioDetail,
  // 환자사정 클래스 004 - 근력사정
  2503: {
    id: 2503,
    title: '근력사정',
    classId: '004',
    learningObjectives: [
      '사지를 하나씩 선택하여 들기·움직이기를 지시하고, 중력과 저항에 대항하는 사지의 근력을 사정할 수 있다.',
      '사지 근력을 0~5단계 척도로 정확히 분류하여 운동 경로의 기능적 손상 정도를 판단할 수 있다.',
      '항목별 정답 확인을 통해 자신의 사정 오류를 파악하고, 근력사정의 정확도를 향상시킬 수 있다.',
    ],
    duration: '7분',
    supportedDevices: ['Mobile'],
    platform: 'Mobile',
    videoUrl: '',
    handover: '외과병동에 입원 중인 환자들을 대상으로 다양한 케이스를 통해 근력사정을 실습하세요.',
    keyTasks: ['상지 근력', '하지 근력', '등급 기록'],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'new-nurse',
    contentCode: 'motor_power',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: '7분',
    averageScore: 0,
    checklist: [],
    resultMetadata: {
      stats: [
        { label: '정답율', value: '88', sub: '%', color: 'text-green-600' },
        { label: '정답수/전체', value: '8', sub: '/ 10', color: 'text-blue-600' },
        { label: '소요시간', value: '6', sub: '', color: 'text-gray-600' }
      ],
      gcsPerformance: [],
      motorPowerPerformance: [
        { no: 1, name: '이주영', gender: 'F', age: '33', left_arm: '3', right_arm: '3', left_leg: '2', right_leg: '3', correct_left_leg: '3', completed_missed: 'Missed', comment: '' },
        { no: 2, name: '환자 B', gender: '여', age: '38세', left_arm: '4', right_arm: '4', left_leg: '3', right_leg: '3', completed_missed: 'Completed', comment: '' },
        { no: 3, name: '환자 C', gender: '남', age: '61세', left_arm: '3', right_arm: '2', left_leg: '3', right_leg: '3', correct_right_arm: '3', completed_missed: 'Missed', comment: '우측 상지 재측정' },
      ]
    }
  } as ScenarioDetail,
  // 주사술기 클래스 005 - 정맥주사
  2601: {
    id: 2601,
    title: '정맥주사',
    classId: '005',
    learningObjectives: [
      '투약 처방에 근거하여 정확한 환자와 약물을 확인하고, 주입 부위의 혈관 상태를 사정할 수 있다.',
      '약물의 특성과 환자의 신체 상태에 따른 적절한 정맥주사 부위 및 바늘 굵기를 판단할 수 있다.',
      '표준 정맥주사 절차에 따라 무균술을 준수하여 정맥 천자를 수행하고, 처방에 따른 주입 속도를 정확하게 설정할 수 있다.',
    ],
    duration: '8분',
    supportedDevices: ['Mobile'],
    platform: 'Mobile',
    videoUrl: '',
    handover: '다양한 진단과 처방을 가진 환자들에게 정맥주사 투약이 필요한 상황입니다. 처방 내용과 환자 상태를 확인하고 정맥주사 술기를 수행하세요.',
    keyTasks: ['물품선택', '수액선택'],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'new-nurse',
    contentCode: 'iv_injection',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: '8분',
    averageScore: 85,
    checklist: [],
    resultMetadata: {
      stats: [
        { label: '정답율', value: '92', sub: '%', color: 'text-green-600' },
        { label: '정답수/전체', value: '1', sub: '/ 2', color: 'text-gray-600' },
        { label: '소요시간', value: '12:45', sub: '', color: 'text-gray-600' }
      ],
      gcsPerformance: [],
      ivPatientInfo: { name: '이시온', age: 29, gender: 'F', registrationNo: '10674187' },
      ivPrescribedOrder: '0.9% N/S 1L Bag 60cc/hr IV',
      ivPerformance: [
        {
          no: 1,
          evaluationItem: '물품선택',
          prescribed: '[처방물품] 토니켓, 수액세트, 카테터, 소독솜, 필름 드레싱',
          userSelection: '[선택물품] 토니켓, 수액세트, 카테터, 소독솜, 필름 드레싱',
          result: '정답',
          score: '20점',
          comment: ''
        },
        {
          no: 2,
          evaluationItem: '수액선택',
          prescribed: '[처방수액] 0.9%N/S 1L',
          userSelection: '[선택수액] 20% Mannitol 100ml',
          result: '오답',
          score: '0점',
          comment: ''
        }
      ]
    }
  } as ScenarioDetail,
  // 주사술기 클래스 005 - 채혈
  2602: {
    id: 2602,
    title: '채혈',
    classId: '005',
    learningObjectives: [
      '진단검사 처방에 따른 정확한 환자를 확인하고, 원활한 채혈을 위한 적절한 부위와 혈관의 개방성을 사정할 수 있다.',
      '처방된 검사 종목(일반혈액, 혈액응고 등)에 적합한 검체 용기(EDTA, Citrate, SST 등)를 정확하게 선택할 수 있다.',
      '표준 채혈 절차를 수행하고, 검체 용기별 올바른 순서와 혼합 지침에 따라 검체를 담을 수 있다.',
    ],
    duration: '6분',
    supportedDevices: ['Mobile'],
    platform: 'Mobile',
    videoUrl: '',
    handover: '다양한 진단과 검사 처방을 가진 환자들에게 채혈이 필요한 상황입니다. 검사 처방과 환자 상태를 확인하고 채혈 술기를 수행하세요.',
    keyTasks: ['물품선택', '채혈관 선택'],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'new-nurse',
    contentCode: 'blood_draw',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: '6분',
    averageScore: 88,
    checklist: [],
    resultMetadata: {
      stats: [
        { label: '정답율', value: '88', sub: '%', color: 'text-green-600' },
        { label: '정답수/전체', value: '2', sub: '/ 2', color: 'text-gray-600' },
        { label: '소요시간', value: '8:20', sub: '', color: 'text-gray-600' }
      ],
      gcsPerformance: [],
      bloodDrawPatientInfo: { name: '강지훈', age: 37, gender: 'M', registrationNo: '10674187' },
      bloodDrawPrescribedOrder: '장염진단 하에 설사, 구토 증상을 호소하는 환자로 전해질불균형 발생\n검사처방 : Electrolyte(K, Na)',
      bloodDrawPerformance: [
        {
          no: 1,
          evaluationItem: '물품선택',
          prescribed: '[처방] 멸균장갑, 알코올솜, 채혈침, EDTA관, SST관',
          userSelection: '[선택] 멸균장갑, 알코올솜, 채혈침, EDTA관, SST관',
          result: '정답',
          score: '25점',
          comment: ''
        },
        {
          no: 2,
          evaluationItem: '채혈관 선택',
          prescribed: '[처방] EDTA 2mL (보라색), SST 5mL (황색)',
          userSelection: '[선택] EDTA 2mL (보라색), SST 3mL (황색)',
          result: '오답',
          score: '0점',
          comment: 'SST 5mL 사용 권장'
        }
      ]
    }
  } as ScenarioDetail,
  // 호흡기감염병 대응 클래스 007 - 중소병원 대응 실습
  2801: {
    id: 2801,
    title: '중소병원 대응 실습',
    classId: '007',
    learningObjectives: [
      'COVID-19 의심 환자의 활력징후와 주관적 증상을 사정할 수 있다.',
      'Level D 개인보호구 착용 및 코호트 격리를 시행하고, SBAR를 통해 감염병 확진 및 전원 필요성을 보고할 수 있다.',
      '신속항원·PCR 검사와 처방된 약물 요법을 수행하고, 확진자 발생에 따른 병실 소독 및 폐기물 관리를 할 수 있다.',
    ],
    duration: '12분',
    supportedDevices: ['Mobile'],
    platform: 'Mobile',
    videoUrl: '',
    handover: '77세 남성 환자로 내원 2일 전부터 마른 기침, 미열, 호흡곤란이 지속되어 외래 통해 Pneumonia로 입원한 환자에게 격리지침에 따른 적절한 간호를 수행해 보세요.',
    keyTasks: ['개인보호구 착의', '개인보호구 탈의', 'Level D 보호구 착의', 'Level D 보호구 탈의', 'PCR 검사'],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'disaster-emergency',
    contentCode: 'covid19',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: '12분',
    averageScore: 85,
    checklist: [],
    resultMetadata: {
      stats: [
        { label: '정답율', value: '85', sub: '%', color: 'text-green-600' },
        { label: '정답수/전체', value: '4', sub: '/ 5', color: 'text-gray-600' },
        { label: '소요시간', value: '11:30', sub: '', color: 'text-gray-600' }
      ],
      gcsPerformance: [],
      respInfectItemRates: [
        { evaluationItem: '개인보호구 착의', correctRate: '90' },
        { evaluationItem: '개인보호구 탈의', correctRate: '88' },
        { evaluationItem: 'Level D 보호구 착의', correctRate: '82' },
        { evaluationItem: 'Level D 보호구 탈의', correctRate: '80' },
        { evaluationItem: 'PCR 검사', correctRate: '85' }
      ],
      respInfectPerformance: [
        {
          evaluationItem: '개인보호구 착의',
          contentItems: [
            { label: '손위생' },
            { label: '가운착용' },
            { label: '마스크 착용' },
            { label: '안면보호구 착용', isError: true },
            { label: '장갑착용' }
          ],
          correctRate: '80',
          comment: '안면보호구 착용 순서 확인 권장'
        }
      ]
    }
  } as ScenarioDetail,
  // ACLS 클래스 008 - 시나리오 1 기본실습
  2901: {
    id: 2901,
    title: '시나리오 1 기본실습',
    classId: '008',
    learningObjectives: [
      '반응 없는 환자의 의식·맥박·호흡을 사정하고, DNR 확인 및 전문소생술 팀을 호출하여 역할을 분배할 수 있다.',
      '심전도 리듬(심실세동·무맥성 심실빈맥)을 확인하고, 알고리즘에 따라 가슴압박과 인공호흡, 리듬 확인, 제세동을 수행할 수 있다.',
      '처방된 약물(에피네프린·아미오다론)을 정확히 투여하고, ROSC 후 소생 후 처치를 수행할 수 있다.',
    ],
    duration: '15분',
    supportedDevices: ['Mobile'],
    platform: 'Mobile',
    videoUrl: '',
    handover: '진행단계에 모든 가이드가 제공됩니다.3VD로 인해 CABG 시행한 과거력 있는 환자로 3일 전 UTI에 의한 Sepsis로 ER 통해 입원하여 치료중입니다. 금일 아침 갑작스러운 흉통 호소로 모니터 위해 치료실로 이동한 환자의 상태를 사정하고 적절한 간호를 제공하세요.',
    keyTasks: ['초기사정', '제세동', '가슴압박', '약물투약', '인공호흡', '자발순환회복 후 처치', '리듬확인', '심정지 원인 파악'],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'disaster-emergency',
    contentCode: 'acls',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: '15분',
    averageScore: 82,
    checklist: [],
    resultMetadata: {
      stats: [
        { label: '정답율', value: '82', sub: '%', color: 'text-green-600' },
        { label: '정답수/전체', value: '6', sub: '/ 8', color: 'text-gray-600' },
        { label: '소요시간', value: '14:20', sub: '', color: 'text-gray-600' }
      ],
      gcsPerformance: [],
      aclsItemRates: [
        { evaluationItem: '초기사정', correctRate: '88' },
        { evaluationItem: '제세동', correctRate: '95' },
        { evaluationItem: '가슴압박', correctRate: '90' },
        { evaluationItem: '약물투약', correctRate: '75' },
        { evaluationItem: '인공호흡', correctRate: '82' },
        { evaluationItem: '자발순환회복 후 처치', correctRate: '78' },
        { evaluationItem: '리듬확인', correctRate: '85' },
        { evaluationItem: '심정지 원인 파악', correctRate: '72' }
      ],
      aclsPerformance: [
        { evaluationItem: '초기사정', prescribed: '[기준] 의식·호흡·맥박 확인', userSelection: '[수행] 의식·호흡·맥박 확인 완료', result: '정답', comment: '' },
        { evaluationItem: '제세동', prescribed: '[기준] 120~200J 제세동 1회', userSelection: '[수행] 150J 제세동 수행', result: '정답', comment: '' },
        { evaluationItem: '가슴압박', prescribed: '[기준] 5~6cm 깊이, 100~120회/분', userSelection: '[수행] 적정 깊이·속도 유지', result: '정답', comment: '' },
        { evaluationItem: '약물투약', prescribed: '[기준] 에피네프린 1mg IV', userSelection: '[수행] 아드레날린 0.5mg 투여', result: '오답', comment: '에피네프린 1mg IV 권장' },
        { evaluationItem: '인공호흡', prescribed: '[기준] 30:2 또는 10회/분', userSelection: '[수행] 30:2 비율 유지', result: '정답', comment: '' },
        { evaluationItem: '자발순환회복 후 처치', prescribed: '[기준] ROSC 후 즉시 후처치', userSelection: '[수행] ROSC 후 ABC 재확인', result: '정답', comment: '' },
        { evaluationItem: '리듬확인', prescribed: '[기준] 2분마다 리듬 확인', userSelection: '[수행] 2분 간격 리듬 확인', result: '정답', comment: '' },
        { evaluationItem: '심정지 원인 파악', prescribed: '[기준] H’s and T’s 점검', userSelection: '[수행] 일부 항목만 점검', result: '오답', comment: '가역적 원인 전 항목 점검' }
      ]
    }
  } as ScenarioDetail,
  // 질환별 환자간호 클래스 010 - 폐렴
  3101: {
    id: 3101,
    title: '폐렴',
    classId: '010',
    learningObjectives: [
      '호흡 양상과 산소포화도를 포함한 활력징후 및 전반적인 신체 상태를 사정할 수 있다.',
      '사정 결과를 바탕으로 환자의 상태 변화를 판단하고, SBAR 형식으로 의료진에게 보고할 수 있다.',
      '산소요법과 흡인 간호를 우선 수행하고, 추가 처방에 따른 투약 간호 후 환자 반응을 평가할 수 있다.',
    ],
    duration: '15분',
    supportedDevices: ['VR'],
    platform: 'VR',
    videoUrl: '',
    handover: '진행단계와 술기에 모든 가이드가 제공됩니다.호흡곤란으로 입원한 환자에 대해 초기 입원 간호 및 환자 상태 변화에 따라 의료진 간 소통을 통해 적절한 간호를 수행해 보세요.',
    keyTasks: ['간호중재', '의사 보고', '검사 수행'],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'nursing-college',
    contentCode: 'pneumonia',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: '15분',
    averageScore: 85,
    checklist: [],
    resultMetadata: {
      stats: [
        { label: '정답율', value: '50', sub: '%', color: 'text-gray-600' },
        { label: '정답수/전체', value: '1', sub: '/ 2', color: 'text-gray-600' },
        { label: '소요시간', value: '12:30', sub: '', color: 'text-gray-600' }
      ],
      gcsPerformance: [],
      diseaseCareItemRates: [
        { evaluationItem: '환자안전', correctRate: '80' },
        { evaluationItem: '환자사정', correctRate: '75' },
        { evaluationItem: '감염관리', correctRate: '82' },
        { evaluationItem: '의사소통', correctRate: '78' },
        { evaluationItem: '술기절차', correctRate: '76' }
      ],
      diseaseCarePerformance: [
        {
          evaluationItem: '환자안전',
          contentParts: [
            { text: '정확한 환자확인 - [기본] 이름 확인, ' },
            { text: '[기본] 생년월일 확인', isError: true }
          ],
          result: '미수행',
          comment: ''
        },
        {
          evaluationItem: '환자안전',
          contentParts: [
            { text: '검체 라벨, 침상 옆 환자 정보지 등을 올바르게 적용함 - [기본] 팔찌 확인' }
          ],
          result: '완료',
          comment: '침상 옆에서 환자 확인 후 라벨 적용 권장'
        }
      ]
    }
  } as ScenarioDetail,
  // 질환별 환자간호 클래스 010 - 서맥 (역량 평가 형식)
  3102: {
    id: 3102,
    title: '서맥',
    classId: '010',
    learningObjectives: [
      '심음 청진과 ECG를 포함한 활력징후 및 전반적인 신체 상태를 사정할 수 있다.',
      '사정 결과를 바탕으로 환자의 상태 변화를 판단하고, SBAR 형식으로 의료진에게 보고할 수 있다.',
      '산소요법과 처방된 약물을 투여하고, 경피적 심장박동조율(TCP) 적용 후 환자 반응을 평가할 수 있다.',
    ],
    duration: '15분',
    supportedDevices: ['VR'],
    platform: 'VR',
    videoUrl: '',
    handover: '진행단계와 술기에 모든 가이드가 제공됩니다.최근 흉부 불편감, 어지럼증이 발생하여 원인규명을 위해 입원한 환자의 초기 입원 간호 및 환자 상태 변화에 따라 의료진 간 소통을 통해 적절한 간호를 수행해 보세요.',
    keyTasks: ['환자 확인', '검체 라벨 적용'],
    simulationExamples: [],
    createdDate: '2026-01-01',
    updatedDate: '2026-01-01',
    ContributedBy: 'Medicrew',
    category: 'nursing-college',
    contentCode: 'bradycardia',
    views: 0,
    simulationCount: 0,
    userCount: 0,
    status: 'active',
    isPublic: true,
    version: '1.0.0',
    averageDuration: '15분',
    averageScore: 78,
    checklist: [],
    resultMetadata: {
      stats: [
        { label: '정답율', value: '78', sub: '%', color: 'text-gray-600' },
        { label: '정답수/전체', value: '4', sub: '/ 5', color: 'text-gray-600' },
        { label: '소요시간', value: '13:20', sub: '', color: 'text-gray-600' }
      ],
      gcsPerformance: [],
      competencyItemRates: [
        { evaluationItem: '환자안전', correctRate: '80' },
        { evaluationItem: '환자 사정', correctRate: '75' },
        { evaluationItem: '감염 관리', correctRate: '82' },
        { evaluationItem: '의사소통', correctRate: '78' },
        { evaluationItem: '술기 역량', correctRate: '76' }
      ],
      competencyPerformance: [
        {
          evaluationItem: '환자안전',
          contentParts: [
            { text: '정확한 환자확인 - [기본] 이름 확인, ' },
            { text: '[기본] 생년월일 확인', isError: true }
          ],
          result: '미수행',
          comment: ''
        },
        {
          evaluationItem: '환자안전',
          contentParts: [
            { text: '검체 라벨, 침상 옆 환자 정보지 등을 올바르게 적용함 - [기본] 팔찌 확인' }
          ],
          result: '완료',
          comment: '침상 옆에서 환자 확인 후 라벨 적용 권장'
        }
      ]
    }
  } as ScenarioDetail,

  ...CURRICULUM_SCENARIO_DETAILS_MORE,
  ...CURRICULUM_SHEET_REMAINING,
};
